import React from 'react';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { TYPOGRAPHY } from 'shared/design-system/settings/variables/Typography';
import styled, { css } from 'styled-components';

export const textSelectionNone = css`
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

const BaseHeading = styled.div`
  font-family: ${TYPOGRAPHY.fontFamilyHeading};
  letter-spacing: -0.02em;
  text-align: ${({ align }: TextProps) => align || 'inherit'};
  ${({ onClick }: TextProps) => (onClick ? 'cursor: pointer;' : '')};
`;

const BaseBody = styled.div`
  font-family: ${TYPOGRAPHY.fontFamilyBody};
  letter-spacing: -0.02em;
  text-align: ${({ align }: TextProps) => align || 'inherit'};
  ${({ allowTextSelection }: TextProps) =>
    allowTextSelection ? '' : textSelectionNone};
  ${({ onClick }: TextProps) => (onClick ? 'cursor: pointer;' : '')};
`;

type TextProps = {
  onClick: TrueImpactTextProps['onClick'];
  allowTextSelection: TrueImpactTextProps['allowTextSelection'];
  color: TrueImpactTextProps['color'];
  align: TrueImpactTextProps['align'];
};

const HeadingPage = styled(BaseHeading)`
  font-size: ${TYPOGRAPHY.headingPage};
  font-weight: ${TYPOGRAPHY.fontWeightBold};
  line-height: ${TYPOGRAPHY.headingPageLineHeight};
  color: ${({ color }: TextProps) => COLORS[color || 'blackBase']};
  margin-bottom: ${SPACERS.small};
`;

const Heading = styled(BaseHeading)`
  font-size: ${TYPOGRAPHY.heading};
  font-weight: ${TYPOGRAPHY.fontWeightBold};
  line-height: ${TYPOGRAPHY.headingLineHeight};
  margin-bottom: ${SPACERS.small};
  color: ${({ color }: TextProps) => COLORS[color || 'blackBase']};
`;

const HeadingSubheading = styled(BaseHeading)`
  font-size: ${TYPOGRAPHY.headingSubheading};
  font-weight: ${TYPOGRAPHY.fontWeightBold};
  line-height: ${TYPOGRAPHY.headingSubheadingLineHeight};
  margin-bottom: ${SPACERS.small};
  color: ${({ color }: TextProps) => COLORS[color || 'blackBase']};
`;

const HeadingLabel = styled(BaseHeading)`
  font-size: ${TYPOGRAPHY.headingLabel};
  text-transform: ${TYPOGRAPHY.textTransformUppercase};
  font-weight: ${TYPOGRAPHY.fontWeightBold};
  line-height: ${TYPOGRAPHY.headingLabelLineHeight};
  color: ${({ color }: TextProps) => COLORS[color || 'greyDark']};
  margin-bottom: ${SPACERS.small};
`;

const Body = styled(BaseBody)`
  font-size: ${TYPOGRAPHY.bodyBase};
  font-weight: ${TYPOGRAPHY.fontWeightBase};
  line-height: ${TYPOGRAPHY.bodyBaseLineHeight};
  color: ${({ color }: TextProps) => COLORS[color || 'blackBase']};
`;

const BodyLarge = styled(BaseBody)`
  font-size: ${TYPOGRAPHY.bodyLarge};
  font-weight: ${TYPOGRAPHY.fontWeightBase};
  line-height: ${TYPOGRAPHY.bodyLargeLineHeight};
  color: ${({ color }: TextProps) => COLORS[color || 'blackBase']};
`;

const DisplayLarge = styled(BaseBody)`
  font-size: ${TYPOGRAPHY.headingPage};
  font-weight: ${TYPOGRAPHY.fontWeightBold};
  line-height: ${TYPOGRAPHY.displayLarge};
  color: ${({ color }: TextProps) => COLORS[color || 'blackBase']};
`;

const BodyLargeBold = styled(BaseBody)`
  font-size: ${TYPOGRAPHY.bodyLarge};
  font-weight: ${TYPOGRAPHY.fontWeightBold};
  line-height: ${TYPOGRAPHY.bodyLargeLineHeight};
  color: ${({ color }: TextProps) => COLORS[color || 'blackBase']};
`;

const BodyLink = styled(BaseBody)`
  font-size: ${TYPOGRAPHY.bodyBase};
  font-weight: ${TYPOGRAPHY.fontWeightBase};
  line-height: ${TYPOGRAPHY.bodyBaseLineHeight};
  color: ${({ color }: TextProps) => COLORS[color || 'ctaPrimary']};
`;

const BodySmall = styled(BaseBody)`
  font-size: ${TYPOGRAPHY.bodySmall};
  font-weight: ${TYPOGRAPHY.fontWeightBase};
  line-height: ${TYPOGRAPHY.bodySmallLineHeight};
  color: ${({ color }: TextProps) => COLORS[color || 'blackBase']};
`;

const BodySmallBold = styled(BodySmall)`
  font-weight: ${TYPOGRAPHY.fontWeightBold};
`;

const BodySmallItalic = styled(BodySmall)`
  font-style: ${TYPOGRAPHY.fontStyleItalic};
`;

const BodyBold = styled(BaseBody)`
  font-size: ${TYPOGRAPHY.bodyBase};
  font-weight: ${TYPOGRAPHY.fontWeightBold};
  line-height: ${TYPOGRAPHY.bodyBaseLineHeight};
  color: ${({ color }: TextProps) => COLORS[color || 'blackBase']};
`;

const BodyItalic = styled(BaseBody)`
  font-size: ${TYPOGRAPHY.bodyBase};
  font-style: ${TYPOGRAPHY.fontStyleItalic};
  font-weight: ${TYPOGRAPHY.fontWeightBase};
  line-height: ${TYPOGRAPHY.bodyBaseLineHeight};
  color: ${({ color }: TextProps) => COLORS[color || 'blackBase']};
`;

export interface TrueImpactTextProps {
  variant?:
    | 'heading-page'
    | 'heading'
    | 'heading-subheading'
    | 'heading-label'
    | 'body'
    | 'body-large'
    | 'body-large-bold'
    | 'body-link'
    | 'body-small'
    | 'body-small-bold'
    | 'body-small-italic'
    | 'display-large'
    | 'body-bold'
    | 'body-italic';
  className?: string;
  ref?: React.RefObject<HTMLDivElement>;
  align?: 'left' | 'center' | 'right';
  color?: keyof typeof COLORS;
  allowTextSelection?: boolean;
  style?: React.CSSProperties;
  children?:
    | React.ReactElement
    | string
    | number
    | React.ReactNode
    | React.ReactNode[]
    | JSX.Element[];
  onClick?: () => unknown;
}

const TrueImpactText: React.FC<TrueImpactTextProps> = ({
  variant = 'body',
  className,
  align,
  allowTextSelection = true,
  color,
  style,
  ref,
  onClick,
  children,
}) => {
  switch (variant) {
    case 'heading-page':
      return (
        <HeadingPage
          allowTextSelection={allowTextSelection}
          className={className}
          align={align}
          color={color}
          onClick={onClick}
          ref={ref}
          style={style}
        >
          {children}
        </HeadingPage>
      );
    case 'heading':
      return (
        <Heading
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </Heading>
      );
    case 'heading-subheading':
      return (
        <HeadingSubheading
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </HeadingSubheading>
      );
    case 'heading-label':
      return (
        <HeadingLabel
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </HeadingLabel>
      );
    case 'body':
      return (
        <Body
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </Body>
      );
    case 'body-large':
      return (
        <BodyLarge
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </BodyLarge>
      );
    case 'display-large':
      return (
        <DisplayLarge
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </DisplayLarge>
      );
    case 'body-large-bold':
      return (
        <BodyLargeBold
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </BodyLargeBold>
      );
    case 'body-link':
      return (
        <BodyLink
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </BodyLink>
      );
    case 'body-small':
      return (
        <BodySmall
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </BodySmall>
      );
    case 'body-small-bold':
      return (
        <BodySmallBold
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </BodySmallBold>
      );
    case 'body-small-italic':
      return (
        <BodySmallItalic
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </BodySmallItalic>
      );
    case 'body-bold':
      return (
        <BodyBold
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </BodyBold>
      );
    case 'body-italic':
      return (
        <BodyItalic
          allowTextSelection={allowTextSelection}
          className={className}
          onClick={onClick}
          color={color}
          align={align}
          style={style}
          ref={ref}
        >
          {children}
        </BodyItalic>
      );
  }
};

export default TrueImpactText;
